import { useState, useMemo } from 'react';
import { X, Save, ChevronDown, Check, Minus } from 'lucide-react';
import { Modal, Button } from '@components/ui';
import { getDaireIlerleme, anaKalemSablonlari, durumlar } from '@mocks/daireIlerleme';
import { toast } from 'react-toastify';
import clsx from 'clsx';

export default function HizliGuncellemeModal({ isOpen, onClose, daire, blokAdi }) {
    const ilerleme = useMemo(() => getDaireIlerleme(daire?.id), [daire?.id]);

    const [editValues, setEditValues] = useState(() => {
        if (!ilerleme) return {};
        const init = {};
        ilerleme.kalemler.forEach(k => {
            k.altKalemler.forEach(ak => {
                init[ak.altKalemId] = { deger: ak.deger, tip: ak.tip };
            });
        });
        return init;
    });

    const [expandedKalem, setExpandedKalem] = useState(null);

    const handleEdit = (altKalemId, newDeger) => {
        setEditValues(prev => ({ ...prev, [altKalemId]: { ...prev[altKalemId], deger: newDeger } }));
    };

    const getAnaKalemYuzde = (anaKalemId) => {
        const sablon = anaKalemSablonlari.find(k => k.id === anaKalemId);
        if (!sablon) return 0;
        let toplam = 0, count = 0;
        sablon.altKalemler.forEach(alt => {
            const val = editValues[alt.id];
            if (!val) return;
            count++;
            if (val.tip === 'adet') toplam += val.deger ? 1 : 0;
            else toplam += (val.deger || 0) / 100;
        });
        return count > 0 ? Math.round((toplam / count) * 100) : 0;
    };

    const getProgressColor = (y) => y >= 75 ? 'bg-success' : y >= 40 ? 'bg-primary' : y > 0 ? 'bg-warning' : 'bg-bg-tertiary';
    const getProgressTextColor = (y) => y >= 100 ? 'text-success' : y >= 40 ? 'text-primary' : y > 0 ? 'text-warning' : 'text-text-muted';

    const handleSave = () => {
        toast.success(`${daire.no} dairesi güncellendi`);
        onClose();
    };

    if (!daire || !ilerleme) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`⚡ Hızlı Güncelleme — ${daire.no}`} size="lg"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>İptal</Button>
                    <Button variant="primary" onClick={handleSave} icon={<Save size={16} />}>Kaydet</Button>
                </>
            }
        >
            {/* Daire bilgi */}
            <div className="flex items-center gap-3 px-3 py-2 bg-bg-tertiary rounded-lg text-sm mb-3">
                <span className="text-text-muted">{blokAdi}</span>
                <span className="text-text-muted">•</span>
                <span className="text-text-muted">{daire.kat}. Kat</span>
                <span className="text-text-muted">•</span>
                <span className="text-text-primary font-medium">{daire.tip}</span>
            </div>

            {/* Accordion kalemler */}
            <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
                {anaKalemSablonlari.map(anaKalem => {
                    const yuzde = getAnaKalemYuzde(anaKalem.id);
                    const isExpanded = expandedKalem === anaKalem.id;
                    const tamamlananAlt = anaKalem.altKalemler.filter(alt => {
                        const val = editValues[alt.id];
                        if (!val) return false;
                        return val.tip === 'adet' ? val.deger : val.deger >= 100;
                    }).length;

                    return (
                        <div key={anaKalem.id} className={clsx(
                            'rounded-lg border overflow-hidden transition-all',
                            isExpanded ? 'border-primary/30' : 'border-border-light'
                        )}>
                            <button
                                onClick={() => setExpandedKalem(isExpanded ? null : anaKalem.id)}
                                className="w-full flex items-center gap-2.5 p-3 text-left hover:bg-bg-hover transition-colors"
                            >
                                <span className="text-lg">{anaKalem.ikon}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium text-text-primary truncate">{anaKalem.name}</span>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <span className="text-2xs text-text-muted">{tamamlananAlt}/{anaKalem.altKalemler.length}</span>
                                            <span className={clsx('text-xs font-bold', getProgressTextColor(yuzde))}>%{yuzde}</span>
                                        </div>
                                    </div>
                                    <div className="h-1 bg-bg-tertiary rounded-full overflow-hidden mt-1">
                                        <div className={clsx('h-full rounded-full', getProgressColor(yuzde))} style={{ width: `${yuzde}%` }} />
                                    </div>
                                </div>
                                <ChevronDown size={14} className={clsx('text-text-muted transition-transform', isExpanded && 'rotate-180')} />
                            </button>

                            {isExpanded && (
                                <div className="border-t border-border-light/50 bg-bg-primary/50 p-2 space-y-0.5">
                                    {anaKalem.altKalemler.map(alt => {
                                        const val = editValues[alt.id] || { deger: alt.tip === 'adet' ? false : 0, tip: alt.tip };
                                        if (alt.tip === 'adet') {
                                            return (
                                                <button key={alt.id} onClick={() => handleEdit(alt.id, !val.deger)}
                                                    className={clsx('w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg active:scale-[0.98]', val.deger ? 'bg-success/5' : 'hover:bg-bg-hover')}
                                                >
                                                    <div className={clsx('w-5 h-5 rounded border-2 flex items-center justify-center shrink-0', val.deger ? 'bg-success border-success' : 'border-border-light')}>
                                                        {val.deger && <Check size={12} className="text-white" />}
                                                    </div>
                                                    <span className={clsx('text-xs flex-1 text-left', val.deger ? 'text-text-muted line-through' : 'text-text-primary')}>{alt.name}</span>
                                                </button>
                                            );
                                        }
                                        return (
                                            <div key={alt.id} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-bg-hover">
                                                <div className={clsx('w-5 h-5 rounded flex items-center justify-center shrink-0', val.deger >= 100 ? 'bg-success/10' : 'bg-bg-tertiary')}>
                                                    {val.deger >= 100 ? <Check size={12} className="text-success" /> : <Minus size={12} className="text-text-muted" />}
                                                </div>
                                                <span className="text-xs text-text-primary flex-1 truncate">{alt.name}</span>
                                                <input type="range" min="0" max="100" step="5" value={val.deger} onChange={e => handleEdit(alt.id, parseInt(e.target.value))} className="w-20 accent-primary h-1" />
                                                <span className={clsx('text-2xs font-mono font-bold w-7 text-right', getProgressTextColor(val.deger))}>%{val.deger}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
}
